import React, { useState, useEffect } from 'react';
import { GetStaticProps } from 'next';
import Link from 'next/link';
import Authors from 'components/Authors';
import getConfig from 'next/config';
import Layout from 'components/Layout';
import { getRecipes } from 'lib/contentful';
import { IRecipe } from 'interfaces';
import Breadcrumbs from 'components/Breadcrumbs';
import ResourcesHeroWrapper from 'components/ResourcesIndexHeroWrapper';
import Markdown from 'components/Markdown';
import ItemsList from 'components/ResourcesItemsList';

const appConfig = getConfig() || { publicRuntimeConfig: {} };
const {
  publicRuntimeConfig: { previewEnabled },
} = appConfig;

async function fetchData(): Promise<IRecipe[]> {
  return getRecipes();
}

// Custom hero as regular one doesn't fit well
const ResourcesHero = ({ name, description, slug, heroComponent, authors }: IRecipe): React.ReactElement => {
  return (
    <ResourcesHeroWrapper
      title="Recipes"
      description="Upcoming and past recipes to help you improve your marketing and sales efforts."
    >
      <div className="bg-white z-2 xl:rounded-xl xl:shadow-xl relative xl:mt-[-10rem]">
        <div className="xl:w-2/5 rounded-r-xl absolute top-0 bottom-0 right-0" />
        <div className="xl:flex flex-row flex-wrap justify-between">
          <div className="xl:w-1/2">
            <div className="xl:px-8 xl:pb-8 pt-8">
              <h1 className="ck-h2 font-semibold">{name}</h1>
              <p className="ck-h5 mb-10 font-normal text-gray-500">{description}</p>
              <div className="max-w-lg mb-10">
                <Authors authors={authors} />
              </div>
              <Link href={`/resources/recipes/${slug}`}>
                <a className="ck-button ck-button--bold--blue xl:inline-flex hidden cursor-pointer">Read more</a>
              </Link>
            </div>
          </div>

          <div className="xl:text-left xl:w-1/2 xl:flex xl:pl-12 relative items-center justify-between overflow-hidden text-center">
            <div className="xl:px-8 xl:mx-0 bg-navy-500 bg-gradient-to-tr from-navy-900 via-blue-800 to-blue-300 flex items-center justify-center w-full h-full mx-auto rounded-r">
              {heroComponent ? (
                <div className="xl:mt-0 mt-8">
                  <Markdown>{heroComponent}</Markdown>
                </div>
              ) : (
                <Link href={`/resources/recipes/${slug}`}>
                  <a className="ck-button ck-button--bold--blue xl:inline-flex hidden cursor-pointer">Read more</a>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </ResourcesHeroWrapper>
  );
};

function Recipes({ recipes: initialRecipes }: { recipes: IRecipe[] }): React.ReactElement {
  const [recipes, setRecipes] = useState<IRecipe[]>(initialRecipes);
  useEffect(() => {
    if (previewEnabled && typeof window !== 'undefined') {
      fetchData().then((newRecipes) => setRecipes(newRecipes));
    }
  }, []);

  const highlightRecipe = recipes[0];
  return (
    <Layout title="Clearbit Recipes" description="Clearbit Recipes" path="recipes">
      <Breadcrumbs crumbs={[{ link: '/resources', copy: 'Resources' }, { copy: 'Recipes' }]} />

      <ResourcesHero
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...highlightRecipe}
      />

      <div className="main-container">
        <div className="mt-10 mb-4 border-t border-gray-100" />
      </div>

      <ItemsList
        urlFn={(slug: string) => `/resources/recipes/${slug}`}
        items={recipes
          ?.filter((recipe) => recipe.slug !== highlightRecipe?.slug)
          .map(({ slug, name, description, authors }) => ({
            slug,
            title: name,
            description,
            authors,
            cta: 'Read more',
          }))}
      />

      <div className="main-container">
        <div className="mt-10 mb-4 border-t border-gray-100" />
      </div>
    </Layout>
  );
}
export const getStaticProps: GetStaticProps = async () => {
  const data = await fetchData();
  return { props: { recipes: data } };
};
export default Recipes;
